import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  faSort,
  faSortUp,
  faSortDown,
  faFileSignature,
  faEllipsisVertical,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Table, { getDensityClass } from '../../Components/Table/Table';
import { percent, useFinancialVisible } from '../financialDisplay';
import StatusBadge from '../../Components/StatusBadge/StatusBadge';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import type { PaginationProps } from '@frontend/Models/Table/TableModels';
import {
  BID_STATUSES,
  deleteBid,
  formatBidMoney,
  updateBid,
  type Bid,
  type BidStatus,
} from '../bidStore';

type SortKey = 'name' | 'status' | 'client' | 'estimator' | 'value' | 'duedate' | 'createddate';
type SortDir = 'asc' | 'desc' | null;

interface BidsTableProps {
  data: Bid[] | null;
  showEstimator?: boolean;
  pageSize?: number;
  pagination?: PaginationProps;
}

interface EditData {
  name?: string;
  status?: BidStatus;
  dueDate?: string;
  // No `client`: the company is derived from the bid's client contact and is not editable here.
}

const STATUS_VARIANT: Record<BidStatus, 'active' | 'at-risk' | 'in-review' | 'completed' | 'pending' | 'open'> = {
  Draft: 'open',
  'In Review': 'in-review',
  Submitted: 'pending',
  Won: 'active',
  Lost: 'at-risk',
};

const STATUS_COLOR: Record<BidStatus, string> = {
  Draft: '#6c757d',
  'In Review': '#17a2b8',
  Submitted: '#ffc107',
  Won: '#28a745',
  Lost: '#dc3545',
};

const BidsTable = ({
  data,
  showEstimator = true,
  pageSize = 7,
  pagination,
}: BidsTableProps): JSX.Element => {
  useFinancialVisible();   // re-render when financial visibility resolves
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bidToDelete, setBidToDelete] = useState<string | null>(null);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editData, setEditData] = useState<EditData>({});
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const navigate = useNavigate();

  const openBid = (id: string): void => {
    navigate(`/bids/${id}`);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (openMenuId) {
        const menuRef = menuRefs.current[openMenuId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const toggleSort = (key: SortKey): void => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      return;
    }
    setSortDir((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
  };

  const getSortValue = (key: SortKey, row: Bid): string | number => {
    switch (key) {
      case 'name': return row.name || '';
      case 'status': return row.status || '';
      case 'client': return row.client || '';
      case 'estimator': return row.estimator || '';
      case 'value': return row.bidValue || 0;
      case 'duedate': return row.dueDate || '';
      case 'createddate': return row.createdDate || '';
      default: return '';
    }
  };

  const displayedData = useMemo(() => {
    if (!data || !sortBy || !sortDir) return data;
    const comparator = (a: Bid, b: Bid) => {
      const va = getSortValue(sortBy, a);
      const vb = getSortValue(sortBy, b);
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      if (sortBy === 'duedate' || sortBy === 'createddate') {
        const da = Date.parse(String(va));
        const db = Date.parse(String(vb));
        if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    };
    const sorted = [...data].sort(comparator);
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [data, sortBy, sortDir]);

  const SortHeader = ({ label, colKey }: { label: string; colKey: SortKey }) => (
    <div
      onClick={() => toggleSort(colKey)}
      style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <span>{label}</span>
      <span>
        {sortBy === colKey && sortDir === 'asc' && <FontAwesomeIcon icon={faSortUp} />}
        {sortBy === colKey && sortDir === 'desc' && <FontAwesomeIcon icon={faSortDown} />}
        {(sortBy !== colKey || sortDir === null) && <FontAwesomeIcon icon={faSort} />}
      </span>
    </div>
  );

  const toggleMenu = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const startEdit = (row: Bid): void => {
    setOpenMenuId(null);
    setEditingRowId(row.id);
    setEditData({ name: row.name, status: row.status, dueDate: row.dueDate });
  };

  const cancelEdit = (): void => {
    setEditingRowId(null);
    setEditData({});
  };

  const saveEdit = (): void => {
    if (!editingRowId) return;
    updateBid(editingRowId, {
      name: editData.name,
      status: editData.status,
      dueDate: editData.dueDate,
    });
    cancelEdit();
  };

  const handleField = (field: keyof EditData, value: string): void => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const confirmDelete = (): void => {
    if (bidToDelete) deleteBid(bidToDelete);
    setDeleteModalOpen(false);
    setBidToDelete(null);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const dueDateInfo = (dateStr?: string, status?: BidStatus): { label: string; color: string } => {
    if (!dateStr) return { label: '', color: '#6c757d' };
    if (status === 'Won' || status === 'Lost') return { label: status, color: status === 'Won' ? '#28a745' : '#dc3545' };
    const due = new Date(dateStr);
    if (Number.isNaN(due.getTime())) return { label: '', color: '#6c757d' };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due.getTime() - today.getTime()) / 86_400_000);
    if (diffDays < 0) return { label: `${Math.abs(diffDays)} days overdue`, color: '#dc3545' };
    if (diffDays === 0) return { label: 'Due today', color: '#ffc107' };
    if (diffDays <= 7) return { label: `${diffDays} days left`, color: '#ffc107' };
    return { label: `${diffDays} days left`, color: '#6c757d' };
  };

  if (!data) return <></>;

  const columns = [
    {
      header: <SortHeader label="Bid Name" colKey="name" />,
      accessor: 'name',
      render: (row: Bid) => {
        const isEditing = editingRowId === row.id;
        return (
          <div className="matter-name-cell">
            <div className="matter-icon-wrapper" style={{ backgroundColor: '#6f42c1' }}>
              <FontAwesomeIcon icon={faFileSignature} className="matter-type-icon" />
            </div>
            <div className="matter-name-content">
              {isEditing ? (
                <input
                  type="text"
                  value={editData.name ?? ''}
                  onChange={(e) => handleField('name', e.target.value)}
                  className="inline-edit-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                    else if (e.key === 'Escape') cancelEdit();
                  }}
                />
              ) : (
                <>
                  <div
                    className="matter-name-text matter-name-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => openBid(row.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') openBid(row.id); }}
                  >
                    {row.name || 'Untitled Bid'}
                  </div>
                  <div className="matter-id-text">{row.id}</div>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: <SortHeader label="Client" colKey="client" />,
      accessor: 'client',
      render: (row: Bid) => {
        const isEditing = editingRowId === row.id;
        // Read-only while editing. The company is DERIVED from the bid's client contact, and a
        // free-text box here let the two disagree: the row showed one company while conversion,
        // which prefers the contact, filed the project against a person at another. The contact is
        // picked on the bid detail page, where the option list is available.
        return isEditing ? (
          <div className="client-cell">
            <div className="client-name">{row.client || 'N/A'}</div>
            <div className="client-type">set via Client Contact on the bid</div>
          </div>
        ) : (
          <div className="client-cell">
            <div className="client-name">{row.client || 'N/A'}</div>
            <div className="client-type">{row.area}</div>
          </div>
        );
      },
    },
    ...(showEstimator
      ? [{
          header: <SortHeader label="Estimator" colKey="estimator" />,
          accessor: 'estimator',
          render: (row: Bid) => {
            const name = row.estimator || 'Unassigned';
            return (
              <div className="leader-cell">
                <div className="leader-avatar-placeholder">
                  {name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div className="leader-info">
                  <div className="leader-name">{name}</div>
                  <div className="leader-title">Estimator</div>
                </div>
              </div>
            );
          },
        }]
      : []),
    {
      header: <SortHeader label="Bid Value" colKey="value" />,
      accessor: 'bidValue',
      render: (row: Bid) => (
        <div>
          <div style={{ fontWeight: 600 }}>{formatBidMoney(row.bidValue)}</div>
          <div className="client-type">{percent(row.targetMargin, 100)} margin</div>
        </div>
      ),
    },
    {
      header: <SortHeader label="Status" colKey="status" />,
      accessor: 'status',
      render: (row: Bid) => {
        const isEditing = editingRowId === row.id;
        if (isEditing) {
          return (
            <select
              value={editData.status ?? row.status}
              onChange={(e) => handleField('status', e.target.value)}
              className="inline-edit-select status-select"
            >
              {BID_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          );
        }
        return (
          <StatusBadge
            status={row.status}
            variant={STATUS_VARIANT[row.status]}
            color={STATUS_COLOR[row.status]}
            size="medium"
          />
        );
      },
    },
    {
      header: <SortHeader label="Due Date" colKey="duedate" />,
      accessor: 'dueDate',
      render: (row: Bid) => {
        const isEditing = editingRowId === row.id;
        if (isEditing) {
          const value = (() => {
            const d = new Date(editData.dueDate ?? row.dueDate);
            return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
          })();
          return (
            <input
              type="date"
              value={value}
              onChange={(e) => handleField('dueDate', e.target.value)}
              className="inline-edit-input"
            />
          );
        }
        const info = dueDateInfo(row.dueDate, row.status);
        return (
          <div className="due-date-cell">
            <div className="due-date-text" style={{ color: info.color }}>{formatDate(row.dueDate)}</div>
            <div className="due-date-status" style={{ color: info.color }}>{info.label}</div>
          </div>
        );
      },
    },
    {
      header: <div className="table-action-header"><span className="table-action-header-text">Actions</span></div>,
      render: (row: Bid) => {
        const isEditing = editingRowId === row.id;
        const isMenuOpen = openMenuId === row.id;
        if (isEditing) {
          return (
            <div className="table-action-cell table-action-edit-buttons">
              <button className="table-action-save-button" onClick={saveEdit}>
                <FontAwesomeIcon icon={faSave} className="table-action-button-icon" />
                <span className="table-action-button-text">Save</span>
              </button>
              <button className="table-action-cancel-button" onClick={cancelEdit}>
                <FontAwesomeIcon icon={faTimes} className="table-action-button-icon" />
                <span className="table-action-button-text">Cancel</span>
              </button>
            </div>
          );
        }
        return (
          <div className="table-action-cell" ref={(el) => { if (el) menuRefs.current[row.id] = el; }}>
            <button
              className="table-action-button"
              aria-label={`Actions for ${row.id}`}
              aria-expanded={isMenuOpen}
              onClick={(e) => toggleMenu(row.id, e)}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>
            {isMenuOpen && (
              <div className="table-action-dropdown">
                <button className="table-action-dropdown-item" onClick={() => { setOpenMenuId(null); openBid(row.id); }}>
                  <FontAwesomeIcon icon={faUpRightFromSquare} className="table-action-dropdown-icon" />
                  <span>Open</span>
                </button>
                <button className="table-action-dropdown-item" onClick={() => startEdit(row)}>
                  <FontAwesomeIcon icon={faEdit} className="table-action-dropdown-icon" />
                  <span>Edit</span>
                </button>
                <button
                  className="table-action-dropdown-item table-action-dropdown-item-danger"
                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); setBidToDelete(row.id); setDeleteModalOpen(true); }}
                >
                  <FontAwesomeIcon icon={faTrash} className="table-action-dropdown-icon" />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className={getDensityClass(pageSize)}>
        <Table
          columns={columns}
          data={displayedData}
          emptyMessage="No bids found"
          rowKey="id"
          pagination={pagination}
        />
      </div>
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setBidToDelete(null); }}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this bid? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default BidsTable;
